import { useMemo, useState } from "react";
import { Search } from "lucide-react";
import {
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@lumanity/ui/primitives";
import type { EvidenceDocument } from "@lumanity/ui/evidence";
import { SOURCE_KIND_LABELS, isDisplayItem } from "./sourceModel";
import type { SectionSourceInput, SourceKind } from "./types";
import { documentName } from "./useProjectEvidence";

interface SourcePick {
  kind: SourceKind;
  label: string;
  version_label: string;
}

interface AddSourcesDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  sectionTitle: string;
  documents: EvidenceDocument[];
  /** Documents the section already uses. */
  usedIds: Set<string>;
  saving: boolean;
  onAdd: (sources: SectionSourceInput[]) => void;
}

/**
 * Pick Evidence Base documents to ground a section in (FR-5-02, FR-5-07). A pick
 * can be marked as a Figure or Table display item with its label and version (FR-5-05).
 */
const addLabel = (count: number) => (count === 0 ? "Add sources" : `Add ${count} ${count === 1 ? "source" : "sources"}`);

const AddSourcesDialog = ({ open, onOpenChange, sectionTitle, documents, usedIds, saving, onAdd }: AddSourcesDialogProps) => {
  const [query, setQuery] = useState("");
  const [picked, setPicked] = useState<Record<string, SourcePick>>({});

  const available = useMemo(() => {
    const q = query.trim().toLowerCase();
    return documents.filter((d) => !usedIds.has(d.id) && (!q || `${documentName(d)} ${d.filename}`.toLowerCase().includes(q)));
  }, [documents, usedIds, query]);

  const toggle = (id: string, on: boolean) =>
    setPicked((prev) => {
      const next = { ...prev };
      if (on) next[id] = { kind: "document", label: "", version_label: "" };
      else delete next[id];
      return next;
    });
  const setPick = (id: string, changes: Partial<SourcePick>) => setPicked((prev) => ({ ...prev, [id]: { ...prev[id], ...changes } }));

  const close = (next: boolean) => {
    if (!next) {
      setPicked({});
      setQuery("");
    }
    onOpenChange(next);
  };
  const add = () =>
    onAdd(
      Object.entries(picked).map(([document_id, p]) => ({
        document_id,
        kind: p.kind,
        label: isDisplayItem(p.kind) ? p.label.trim() || null : null,
        version_label: isDisplayItem(p.kind) ? p.version_label.trim() || null : null,
      })),
    );
  const count = Object.keys(picked).length;

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Add sources to {sectionTitle}</DialogTitle>
          <DialogDescription>Choose documents from the project's Evidence Base. Mark figures and tables as display items so their instructions are kept separate.</DialogDescription>
        </DialogHeader>

        <div className="relative">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search documents" aria-label="Search documents" className="pl-8" />
        </div>

        <ul className="max-h-[50vh] overflow-y-auto divide-y rounded-md border">
          {available.length === 0 && (
            <li className="p-4 text-center text-sm text-muted-foreground">
              {documents.length === usedIds.size ? "Every Evidence Base document is already a source for this section." : "No documents match your search."}
            </li>
          )}
          {available.map((doc) => {
            const pick = picked[doc.id];
            const checkboxId = `source-${doc.id}`;
            return (
              <li key={doc.id} className="p-3 space-y-2">
                <div className="flex items-start gap-3">
                  <Checkbox id={checkboxId} checked={!!pick} onCheckedChange={(v) => toggle(doc.id, v === true)} className="mt-0.5" />
                  <label htmlFor={checkboxId} className="flex-1 min-w-0 cursor-pointer">
                    <span className="block truncate text-sm font-medium">{documentName(doc)}</span>
                    {doc.title && <span className="block truncate text-xs text-muted-foreground">{doc.filename}</span>}
                  </label>
                  {pick && (
                    <Select value={pick.kind} onValueChange={(v) => setPick(doc.id, { kind: v as SourceKind })}>
                      <SelectTrigger className="h-8 w-40 text-xs" aria-label={`Use ${documentName(doc)} as`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {(Object.keys(SOURCE_KIND_LABELS) as SourceKind[]).map((k) => (
                          <SelectItem key={k} value={k}>
                            {SOURCE_KIND_LABELS[k]}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                </div>
                {pick && isDisplayItem(pick.kind) && (
                  <div className="ml-7 flex gap-2">
                    <Input
                      value={pick.label}
                      onChange={(e) => setPick(doc.id, { label: e.target.value })}
                      placeholder={pick.kind === "figure" ? "Label, e.g. Figure 1" : "Label, e.g. Table 1"}
                      aria-label="Display item label"
                      maxLength={120}
                      className="h-8 text-sm"
                    />
                    <Input
                      value={pick.version_label}
                      onChange={(e) => setPick(doc.id, { version_label: e.target.value })}
                      placeholder="Version, e.g. v2"
                      aria-label="Display item version"
                      maxLength={60}
                      className="h-8 w-40 text-sm"
                    />
                  </div>
                )}
              </li>
            );
          })}
        </ul>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => close(false)} disabled={saving}>
            Cancel
          </Button>
          <Button type="button" variant="brand" onClick={add} disabled={saving || count === 0}>
            {saving ? "Adding..." : addLabel(count)}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AddSourcesDialog;
